'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import React from 'react';

import {
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Dialog as ShadcnDialog
} from '@ui/components/ui/dialog';
import { cn } from '@ui/lib/utils';

export interface DialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Callback when the dialog should be closed
   */
  onClose: () => void;
  /**
   * Dialog title (optional)
   */
  title?: string;
  /**
   * Dialog description (optional)
   */
  description?: string;
  /**
   * Dialog content
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes for the dialog content
   */
  className?: string;
  /**
   * Size variant of the dialog
   * @default "default"
   */
  size?: 'sm' | 'default' | 'lg' | 'xl';
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
}

const sizeClasses = {
  sm: 'sm:max-w-sm',
  default: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl'
};

/**
 * Custom DialogContent that conditionally renders the close button
 */
const CustomDialogContent = ({
  className,
  children,
  showCloseButton = true,
  onCloseAutoFocus,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & { showCloseButton?: boolean }) => {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
          className
        )}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          event.preventDefault();
          document.body.style.pointerEvents = '';
        }}
        onInteractOutside={(event) => {
          // A popover/select/dropdown opened from inside the dialog renders in a
          // body-level Radix popper portal, so Radix would treat a click on it as
          // "outside" and dismiss the dialog — tearing down any link/button inside
          // that popper before it can act (e.g. the Format tooltip's "edit
          // locations" link). Those poppers are logically part of the dialog, so
          // don't dismiss when the interaction originates inside one.
          const target = event.detail.originalEvent.target as Element | null;
          if (target?.closest('[data-radix-popper-content-wrapper]')) {
            event.preventDefault();
            return;
          }
          onInteractOutside?.(event);
        }}
        {...props}
      >
        <DialogTitle className="sr-only">Dialog</DialogTitle>
        <DialogDescription className="sr-only">Dialog content</DialogDescription>
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close className="ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-6 right-6 cursor-pointer rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};

/**
 * A customizable dialog component that wraps ShadCN Dialog with project-specific defaults
 */
export const Dialog = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  className,
  size = 'default',
  showCloseButton = true
}: DialogProps) => {
  return (
    <ShadcnDialog open={isOpen} onOpenChange={onClose}>
      <CustomDialogContent
        className={cn('flex flex-col gap-4 sm:gap-2', sizeClasses[size], className)}
        showCloseButton={showCloseButton}
      >
        {(title || description) && (
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
        )}
        {children}
      </CustomDialogContent>
    </ShadcnDialog>
  );
};
